"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

type Stat = {
  value: string; // ex: "300+", "60", "24/7"
  label: string;
  prefix?: string;
  suffix?: string;
};

function CountUp({
  target,
  prefix = "",
  suffix = "",
  duration = 1800,
  start,
}: {
  target: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  start: boolean;
}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!start) return;

    const t0 = performance.now();
    const tick = (now: number) => {
      const elapsed = now - t0;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, target, duration]);

  return (
    <span>
      {prefix}
      {value}
      {suffix}
    </span>
  );
}

function StatItem({ stat, index, start }: { stat: Stat; index: number; start: boolean }) {
  const numeric = parseInt(stat.value.replace(/\D/g, ""), 10);
  const hasNumeric = !Number.isNaN(numeric) && numeric > 0;
  const suffix = stat.suffix ?? stat.value.replace(/[\d\s]/g, "");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: "easeOut" }}
      className="text-center md:text-left"
    >
      <span className="block font-heading text-6xl md:text-7xl font-light text-white leading-none">
        {hasNumeric ? (
          <CountUp target={numeric} prefix={stat.prefix} suffix={suffix} start={start} />
        ) : (
          <>{stat.value}</>
        )}
      </span>

      <p className="mt-5 text-[11px] tracking-[0.28em] uppercase text-white/50 font-body">
        {stat.label}
      </p>
    </motion.div>
  );
}

export default function Stats({
  eyebrow,
  stats,
}: {
  eyebrow?: string;
  stats: Stat[];
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section
      ref={ref}
      className="border-y border-dashed border-white/15 bg-[#0b0b0d] py-24 md:py-28"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {eyebrow && (
          <div className="flex items-center justify-center gap-5 mb-16">
            <span className="block h-px w-10 bg-white/20" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/35 font-body">
              {eyebrow}
            </span>
            <span className="block h-px w-10 bg-white/20" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-12 md:grid-cols-4 md:gap-10">
          {stats.map((s, i) => (
            <StatItem key={s.label} stat={s} index={i} start={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}
